import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Company details ───────────────────────────────
const COMPANY = {
  name: 'SwissChem',
  tagline: 'Textile & dye chemicals',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
  email: 'ceo@swisschem.pk',
  website: 'www.swisschem.pk',
  salesTax: '04-05-6200-005-55',
  ntn: '0659603-7',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const fmtNum = (n) =>
  (parseFloat(n) || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtQty = (n) =>
  parseFloat(n || 0).toFixed(2).replace(/\.?0+$/, '');

/**
 * Generate and download a Purchase Order PDF (grey landscape layout).
 * @param {object} po - full purchase order object (with items[]) from getPurchaseOrderById
 */
export const generatePoPdf = (po) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();   // ~297
  const pageHeight = doc.internal.pageSize.getHeight(); // ~210
  const marginX = 12;
  let y = 14;

  // ─── Header: logo box + name (left) | PURCHASE ORDER (center) | address (right) ───
  // Logo box
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.rect(marginX, y - 4, 12, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text('SC', marginX + 6, y + 3.5, { align: 'center' });

  // Company name under/next to logo
  doc.setFontSize(11);
  doc.text(COMPANY.name, marginX + 16, y + 1);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text(COMPANY.tagline, marginX + 16, y + 6);

  // Title center
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(44, 62, 80);
  doc.text('PURCHASE ORDER', pageWidth / 2, y + 3, { align: 'center' });

  // Address right
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  const rightX = pageWidth - marginX;
  doc.text(COMPANY.address, rightX, y - 2, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}`, rightX, y + 2, { align: 'right' });
  doc.text(`Sales Tax No: ${COMPANY.salesTax}`, rightX, y + 6, { align: 'right' });
  doc.text(`National Tax No: ${COMPANY.ntn}`, rightX, y + 10, { align: 'right' });

  y += 16;

  // ─── Bold divider ─────────────────────────────────
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.8);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 8;

  // ─── Vendor Details (left box) + PO Details (right box) ───
  const boxW = (pageWidth - marginX * 2 - 6) / 2;
  const boxTop = y;
  const boxH = 34;

  // Left box — Vendor Details
  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.3);
  doc.rect(marginX, boxTop, boxW, boxH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(26, 60, 110);
  doc.text('Vendor Details:', marginX + 3, boxTop + 5);

  const vLabelX = marginX + 3;
  const vValX = marginX + 32;
  let vy = boxTop + 11;
  const vrow = (label, value) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    doc.text(label, vLabelX, vy);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);
    doc.text(String(value || '—'), vValX, vy);
    vy += 5.5;
  };
  vrow('Vendor Name', po.vendor_name);
  vrow('Vendor Address', po.vendor_address);
  vrow('Sale Tax No', po.vendor_strn);
  vrow('NTN#', po.vendor_ntn);
  vrow('Contact Person', po.vendor_contact_person);

  // Right box — Purchase Order Details
  const rBoxX = marginX + boxW + 6;
  doc.rect(rBoxX, boxTop, boxW, boxH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(26, 60, 110);
  doc.text('Purchase Order Details:', rBoxX + 3, boxTop + 5);

  const pLabelX = rBoxX + 3;
  const pValX = rBoxX + 32;
  const pLabelX2 = rBoxX + boxW / 2 + 3;
  const pValX2 = rBoxX + boxW / 2 + 26;
  let py = boxTop + 11;

  const prow = (l1, v1, l2, v2) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    doc.text(l1, pLabelX, py);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);
    doc.text(String(v1 || '—'), pValX, py);
    if (l2) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(l2, pLabelX2, py);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
      doc.text(String(v2 || '—'), pValX2, py);
    }
    py += 5.5;
  };
  prow('PO Date', formatDate(po.po_date), 'PO #', po.po_no);
  prow('Feed By', po.feed_by, 'Location', po.location);
  prow('Pay Terms', po.payment_terms, 'Delivery', po.delivery);
  prow('Remarks', po.remarks, '', '');

  y = boxTop + boxH + 8;

  // ─── Items table ──────────────────────────────────
  const rows = (po.items || []).map((it) => {
    const qty = parseFloat(it.quantity) || 0;
    const rate = parseFloat(it.rate) || 0;
    const gstPct = parseFloat(it.gst_percent) || 0;
    const amount = qty * rate;
    const gstAmt = amount * gstPct / 100;
    const total = amount + gstAmt;
    return [
      it.product_code || '—',
      it.product_name || '—',
      it.unit || '—',
      fmtQty(qty),
      fmtNum(rate),
      fmtNum(amount),
      String(gstPct),
      fmtNum(gstAmt),
      fmtNum(total),
      po.payment_terms || '—',
      po.delivery || '—',
    ];
  });

  // Totals
  const subtotal = parseFloat(po.subtotal) || 0;
  const gstTotal = parseFloat(po.gst_amount) || 0;
  const grand = parseFloat(po.grand_total) || 0;
  const totalQty = (po.items || []).reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);

  autoTable(doc, {
    startY: y,
    head: [['Item ID', 'Item', 'Uom', 'Qty', 'Rate', 'Amount', 'GST%', 'GST Amt', 'Total Amount', 'Term', 'Delivery']],
    body: rows.length > 0 ? rows : [['—', 'No items', '—', '—', '—', '—', '—', '—', '—', '—', '—']],
    foot: [[
      '', '', '', fmtQty(totalQty), '', fmtNum(subtotal), '', fmtNum(gstTotal), fmtNum(grand), '', '',
    ]],
    theme: 'grid',
    headStyles: {
      fillColor: [232, 232, 232],
      textColor: [40, 40, 40],
      fontStyle: 'bold',
      fontSize: 8,
      lineColor: [150, 150, 150],
      lineWidth: 0.2,
    },
    bodyStyles: { fontSize: 8, textColor: [40, 40, 40], lineColor: [150, 150, 150], lineWidth: 0.2 },
    footStyles: {
      fillColor: [245, 245, 245],
      textColor: [40, 40, 40],
      fontStyle: 'bold',
      fontSize: 8,
      lineColor: [150, 150, 150],
      lineWidth: 0.2,
    },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 12, halign: 'center' },
      3: { cellWidth: 16, halign: 'right' },
      4: { cellWidth: 20, halign: 'right' },
      5: { cellWidth: 24, halign: 'right' },
      6: { cellWidth: 12, halign: 'center' },
      7: { cellWidth: 22, halign: 'right' },
      8: { cellWidth: 26, halign: 'right' },
      9: { cellWidth: 16 },
      10: { cellWidth: 18 },
    },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 8;

  // ─── Totals box (right side) ──────────────────────
  const totBoxW = 70;
  const totBoxX = pageWidth - marginX - totBoxW;
  const totRows = [
    ['Total Amount', fmtNum(subtotal)],
    ['Sales Tax Amount', fmtNum(gstTotal)],
    ['WHT Amount', '.00'],
    ["Other's Exp.", '.00'],
    ['Grand Amount', fmtNum(grand)],
  ];
  let ty = afterY;
  doc.setFontSize(8.5);
  totRows.forEach((r, i) => {
    const isGrand = i === totRows.length - 1;
    if (isGrand) {
      doc.setFillColor(240, 240, 240);
      doc.rect(totBoxX, ty - 4, totBoxW, 7, 'F');
    }
    doc.setFont('helvetica', isGrand ? 'bold' : 'normal');
    doc.setTextColor(40, 40, 40);
    doc.text(r[0], totBoxX + 2, ty);
    doc.text(r[1], totBoxX + totBoxW - 2, ty, { align: 'right' });
    ty += isGrand ? 7 : 6;
  });
  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.3);
  doc.rect(totBoxX, afterY - 5, totBoxW, ty - afterY + 1);

  // ─── Signatures ───────────────────────────────────
  let sigY = Math.max(ty + 16, pageHeight - 40);
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(0.3);
  const sigW = 60;
  const gap = (pageWidth - marginX * 2 - sigW * 3) / 2;
  const sigs = ['Order By', 'Approved By', 'Audit Approved By'];
  sigs.forEach((label, i) => {
    const sx = marginX + i * (sigW + gap);
    doc.line(sx, sigY, sx + sigW, sigY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text(label, sx + sigW / 2, sigY + 5, { align: 'center' });
  });

  // ─── Terms & Conditions ───────────────────────────
  let termsY = sigY + 14;
  if (termsY > pageHeight - 34) termsY = pageHeight - 34; // keep on page
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  doc.text('TERMS & CONDITIONS:', marginX, termsY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(90, 90, 90);
  const terms = [
    '1) Credit days will be started after receiving invoices.',
    '2) Send bill / invoice within 3 days after delivery.',
    '3) We reserve the rights to cancel PO at any time without intimation.',
    '4) Send 2 copies of delivery challan along with PO copy.',
    '5) No payments, gifts, or gratuities of any kind shall be made directly or indirectly to SwissChem employees.',
    '6) Delivery must be as per agreed lead time.',
    '7) Vendor should be responsible for the proper packing of the goods.',
    '8) Vendor shall bear the expense of loss or damage of goods until provided to the destination.',
  ];
  let tyy = termsY + 4;
  terms.forEach((t) => { doc.text(t, marginX, tyy); tyy += 3.6; });

  // ─── Page number (bottom-right) ───────────────────
  doc.setFontSize(7.5);
  doc.setTextColor(150, 150, 150);
  doc.text('Page 1 of 1', pageWidth - marginX, pageHeight - 6, { align: 'right' });

  // ─── Save ─────────────────────────────────────────
  doc.save(`${po.po_no || 'PurchaseOrder'}.pdf`);
};